<script setup lang="ts">
import { onMounted, onUpdated, ref } from 'vue'

defineProps<{
  content: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}>()

// PrimeVue's directive is hover-only by default, and its `.focus` modifier turns
// hover off — there is no "both" (BUG-028). Keep the hover binding and replay
// focus / blur as mouseenter / mouseleave so keyboard users get the tooltip too.
const wrapperRef = ref<HTMLElement | null>(null)

// A wrapped Button / link is already a tab stop; only a plain icon or text
// needs the wrapper itself to take focus.
const FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
const isSelfFocusable = ref(false)

function syncFocusable() {
  isSelfFocusable.value = !wrapperRef.value?.querySelector(FOCUSABLE)
}

onMounted(syncFocusable)
onUpdated(syncFocusable)

function replay(type: 'mouseenter' | 'mouseleave') {
  wrapperRef.value?.dispatchEvent(new MouseEvent(type))
}
</script>

<template>
  <span
    ref="wrapperRef"
    v-tooltip="{
      value: content,
      position: position ?? 'top',
      showDelay: 200,
      pt: { root: 'app-tooltip' },
    }"
    class="tooltip-wrapper"
    :tabindex="isSelfFocusable ? 0 : undefined"
    @focusin="replay('mouseenter')"
    @focusout="replay('mouseleave')"
  >
    <slot />
  </span>
</template>

<style scoped>
.tooltip-wrapper {
  display: inline-flex;
}

.tooltip-wrapper:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
  border-radius: var(--p-border-radius-sm);
}
</style>
